"use client";

import { useContext, useId, useRef, useState } from "react";
import { Info } from "lucide-react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";
import { FormItemContext } from "@/ui/form";
import { cn } from "@/ui/cn";
import { Button } from "@/ui/button";

export function FormHint({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  // Inside a FormItem the id wires up aria-describedby; outside one (a bare
  // label with a hint) any unique id serves the sr-only span.
  const { id: itemId } = useContext(FormItemContext);
  const fallbackId = useId();
  const formDescriptionId = itemId ? `${itemId}-form-item-description` : fallbackId;
  const [open, setOpen] = useState(false);
  const pointerType = useRef<string>("mouse");

  return (
    <>
      <span id={formDescriptionId} className="sr-only">
        {children}
      </span>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="bare"
            aria-label="More information"
            onPointerDown={(e) => {
              pointerType.current = e.pointerType;
            }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => {
              if (pointerType.current === "touch") setOpen((v) => !v);
            }}
            className={cn(
              "size-3.5 shrink-0 text-muted-foreground hover:text-foreground transition-colors",
              className
            )}
          >
            <Info className="size-3.5" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          className="bg-foreground text-background border-0 shadow-md rounded-md text-xs text-balance max-w-xs w-auto px-3 py-1.5"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {children}
          <PopoverPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
        </PopoverContent>
      </Popover>
    </>
  );
}
